import { useEffect, useState } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { endImpersonation, getRemainingMs } from '../../services/impersonationSession';
import { IMPERSONATION_END_REASONS, IMPERSONATION_NAMESPACE } from '../../common/constants/impersonation';
import { useImpersonation } from '../../hooks/useImpersonation';
import { confirmStopImpersonation } from './confirmStopImpersonation';
import classes from './ImpersonationOverlay.module.scss';

// keep in sync with $bar-height in ImpersonationOverlay.module.scss
const BAR_HEIGHT = '40px';

const formatCountdown = remainingMs => {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');

    return `${minutes}:${seconds}`;
};

const ImpersonationBar = ({ session }) => {
    const { t } = useTranslation([IMPERSONATION_NAMESPACE, 'confirmModal']);
    const [remainingMs, setRemainingMs] = useState(() => getRemainingMs(session));

    // the countdown follows the server clock; at 0 the tab ends itself instead of waiting for a 401
    useEffect(() => {
        let interval = null;

        const tick = () => {
            const remaining = getRemainingMs(session);
            setRemainingMs(remaining);

            if (remaining <= 0) {
                clearInterval(interval);
                endImpersonation(IMPERSONATION_END_REASONS.EXPIRED);
            }

            return remaining > 0;
        };

        if (tick()) {
            interval = setInterval(tick, 1000);
        }

        return () => clearInterval(interval);
    }, [session]);

    // the fixed bar would cover the app's header; push the page down by its height while it is shown
    useEffect(() => {
        const { body, documentElement } = document;
        const previousPaddingTop = body.style.getPropertyValue('padding-top');

        body.style.setProperty('padding-top', BAR_HEIGHT);
        documentElement.style.setProperty('--impersonation-bar-height', BAR_HEIGHT);

        return () => {
            // an empty value removes the property again
            body.style.setProperty('padding-top', previousPaddingTop);
            documentElement.style.removeProperty('--impersonation-bar-height');
        };
    }, []);

    return (
        <>
            <div className={classes.frame} aria-hidden="true" />
            <div className={classes.bar} role="status">
                <span className={classes.target}>
                    {t('bar.viewing-as')} <strong>{session.target?.name}</strong>{' '}
                    <span className={classes.email}>{session.target?.email}</span>
                </span>
                <span className={classes.badge}>{t('bar.read-only')}</span>
                <span className={classes.countdown}>
                    <span className={classes.timeLeftLabel}>{t('bar.time-left')} </span>
                    <span>{formatCountdown(remainingMs)}</span>
                </span>
                <Button size="small" className={classes.stopButton} onClick={() => confirmStopImpersonation(t)}>
                    {t('bar.stop')}
                </Button>
            </div>
        </>
    );
};

/** Red frame and top bar on every page of an impersonation tab, so nobody mistakes it for their own session. */
export const ImpersonationOverlay = () => {
    const { session } = useImpersonation();

    return session ? <ImpersonationBar session={session} /> : null;
};
